function stringValue(value) {
  return String(value ?? "").trim();
}

export function normaliseMedicalInstitutionMemberSearchText(value) {
  return stringValue(value).replace(/\s+/g, " ").toLocaleLowerCase();
}

/**
 * Search results are intentionally query-gated: the protected collections can
 * hydrate in memory, but opening Search must not disclose an unfiltered list.
 */
export function hasMedicalInstitutionSearchQuery(query) {
  return normaliseMedicalInstitutionMemberSearchText(query).length > 0;
}

function memberName(member) {
  return stringValue(member?.full_name || member?.fullName || member?.name || member?.display_name);
}

function memberUserKey(member) {
  return stringValue(member?.userId || member?.user_id || member?.profile_id || member?.email).toLocaleLowerCase();
}

function isActiveMember(member) {
  return normaliseMedicalInstitutionMemberSearchText(member?.status) === "active";
}

function memberSearchValues(member, facilityName) {
  const role = stringValue(member?.role);
  return [
    memberName(member),
    member?.first_name,
    member?.firstName,
    member?.last_name,
    member?.lastName,
    member?.surname,
    member?.email,
    member?.phone,
    role,
    role.replaceAll("_", " "),
    member?.customRoleLabel,
    facilityName,
  ].map(normaliseMedicalInstitutionMemberSearchText).filter(Boolean);
}

function recordForMember(member, facility, source) {
  const facilityId = stringValue(facility?.id);
  const facilityName = stringValue(facility?.name || facility?.facility_name || facility?.title) || "Institution";
  const membershipId = stringValue(member?.id);
  const userId = stringValue(member?.userId || member?.user_id || member?.profile_id);
  const identity = memberUserKey(member);
  const fallbackIdentity = identity || membershipId;
  return {
    ...member,
    source,
    membershipId: membershipId || null,
    userId: userId || null,
    fullName: memberName(member),
    facilityId,
    facilityName,
    membershipKey: `${facilityId || "parent"}:${source}:${membershipId || fallbackIdentity}`,
    facilityIdentityKey: `${facilityId || "parent"}:${fallbackIdentity}`,
    searchValues: memberSearchValues(member, facilityName),
    email: stringValue(member?.email) || null,
    phone: stringValue(member?.phone) || null,
    status: "active",
  };
}

/**
 * The saved-facility Member surface renders active facility memberships first
 * and the active parent workspace members as accountable creators. Search uses
 * the same two already-authorised collections, never a user directory.
 */
export function buildMedicalInstitutionMemberSearchIndex({ facilities = [], parentMembers = [], institutionId = "" } = {}) {
  const records = [];
  const exactMemberships = new Set();
  const facilityMemberIdentities = new Set();
  const activeFacilities = Array.isArray(facilities) ? facilities.filter((facility) => stringValue(facility?.id)) : [];

  const append = (member, facility, source) => {
    if (!isActiveMember(member)) return;
    const record = recordForMember(member, facility, source);
    if (!record.searchValues.length) return;
    if (source === "parent" && facilityMemberIdentities.has(record.facilityIdentityKey)) return;
    if (exactMemberships.has(record.membershipKey)) return;
    exactMemberships.add(record.membershipKey);
    if (source === "facility") facilityMemberIdentities.add(record.facilityIdentityKey);
    records.push(record);
  };

  activeFacilities.forEach((facility) => {
    const facilityMemberships = [
      ...(Array.isArray(facility?.admins) ? facility.admins : []),
      ...(Array.isArray(facility?.members) ? facility.members : []),
    ];
    facilityMemberships.forEach((member) => append(member, facility, "facility"));
  });

  const activeParentId = stringValue(institutionId);
  const authorisedParentMembers = Array.isArray(parentMembers)
    ? parentMembers.filter((member) => !activeParentId || stringValue(member?.institution_id || member?.institutionId) === activeParentId)
    : [];
  // Parent-Institution memberships are themselves authorised records. They
  // must remain visitable even before they are assigned to a facility.
  authorisedParentMembers.forEach((member) => append(member, null, "parent"));

  return records;
}

export function filterMedicalInstitutionMemberSearchIndex(records = [], query = "") {
  const needle = normaliseMedicalInstitutionMemberSearchText(query);
  const source = Array.isArray(records) ? records : [];
  if (!needle) return source;
  return source.filter((record) => (record?.searchValues || []).some((value) => value.includes(needle)));
}
